import * as Sentry from '@sentry/react';
import { sentryDsn } from 'application/constants/env-variables';
import axios from 'axios';
import { Loading } from 'components/uikit/suspense/Loading';
import { config } from 'devextreme/common';
import { addLogoutHandler } from 'helpers/auth/auth-handlers';
import { backendUri } from 'helpers/auth/openid/openid-settings';
import { idempotenceInterceptor } from 'helpers/interceptors/idempotence-interceptor';
import { addAxiosRetry } from 'helpers/interceptors/retry-interceptor';
import { addTraceInterceptor } from 'helpers/interceptors/trace-interceptor';
import { QuerySuspenseErrorWrapper } from 'helpers/retry-helper';
import { anonymousRoutes, authorizedRoutes } from 'pages/router';
import { Suspense, useEffect, useMemo } from 'react';
import { Provider } from 'react-redux';
import { RouterProvider, RouterState } from 'react-router-dom';
import { PersistGate } from 'redux-persist/integration/react';
import { LanguageProvider } from './application/localization/LanguageProvider';
import { RootStore } from './application/redux-store';
import { logoutAction } from './application/redux-store/root-reducer';
import { ModalProvider } from './components/uikit/modal/useModal';
import { sendRefreshTokenRequest } from './helpers/auth/auth-client';
import {
  setupAuthInterceptor,
  useIsAuthorized,
} from './helpers/auth/auth-interceptor';
import { sessionAxiosInterceptor } from './helpers/interceptors/inject-session-interceptor';
import { languageInterceptor } from './helpers/interceptors/language-interceptor';
import { QueryFactory } from './services/api';
import { SignalRConnectionsProvider } from './services/signalR/SignalRConnectionsProvider.tsx';
import { SentryUserBridge } from './application/sentry/SentryUserBridge.tsx';
import { sendRouteChangeEvent } from 'helpers/analytics.ts';
import i18next from 'i18next';
import { ToastContainer } from 'components/uikit/toast/ToastContainer.tsx';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

config({
  licenseKey:
    'ewogICJmb3JtYXQiOiAxLAogICJjdXN0b21lcklkIjogImFhNTlmMjNiLTBhNzAtNDFkOS1iN2NlLWYxZjZlY2IzZDIxZiIsCiAgIm1heFZlcnNpb25BbGxvd2VkIjogMjQyCn0=.xEJ9VlSW0V1KKjrsRG1OL7TJ0N9jtvWrMU4iNAIb59H5+T4tZI0aMPUv3Yc/4qDAT8F1M2X6w7SwX170XUstvBVfHO2Kbuc57d6ngrNRttR3ljVVII+g8U/NK4SDAz466hojAg==',
});
QueryFactory.setAxiosFactory(() => axios);

setupAuthInterceptor(axios, async (authData) => {
  const result = await sendRefreshTokenRequest(authData.refresh_token);
  return result;
});
addLogoutHandler(() => {
  RootStore.store.dispatch(logoutAction);
  window.history.pushState(null, '', backendUri);
});

axios.interceptors.request.use(idempotenceInterceptor);
addAxiosRetry(axios);
axios.interceptors.request.use(languageInterceptor);
axios.interceptors.request.use(sessionAxiosInterceptor);
addTraceInterceptor(axios);

if (import.meta.env.PROD) {
  Sentry.init({
    dsn: sentryDsn(),
    // integrations: [new Integrations.BrowserTracing()],
    // tracesSampleRate: 1.0,
  });
}

export const App = () => {
  const fallback = useMemo(() => {
    return <Loading loading={true} />;
  }, []);
  const isAuth = useIsAuthorized();
  const router = useMemo(
    () => (isAuth ? authorizedRoutes() : anonymousRoutes()),
    [isAuth],
  );
  useEffect(() => {
    function sendTrackEvent(state: RouterState) {
      const customProps = {
        language: i18next.language,
      };
      sendRouteChangeEvent(state, customProps);
    }
    sendTrackEvent(router.state);
    return router.subscribe(sendTrackEvent);
  }, [router]);

  if (isAuth === 'loading') return <Loading loading={true} />;

  return (
    <Suspense fallback={fallback}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <SignalRConnectionsProvider>
          <Provider store={RootStore.store}>
            <PersistGate loading={fallback} persistor={RootStore.persistor}>
              <LanguageProvider>
                <ModalProvider>
                  <QuerySuspenseErrorWrapper>
                    <RouterProvider router={router} />
                    <ToastContainer />
                    {isAuth && <SentryUserBridge />}
                  </QuerySuspenseErrorWrapper>
                </ModalProvider>
              </LanguageProvider>
            </PersistGate>
          </Provider>
        </SignalRConnectionsProvider>
      </LocalizationProvider>
    </Suspense>
  );
};
